/* eslint-disable react-hooks/exhaustive-deps */
import { FC, ReactNode, useContext, useEffect } from "react";
import { NavLink as RouterLink } from "react-router-dom";
import clsx from "clsx";
import PropTypes from "prop-types";
import { SidebarContext } from "../../../../contexts/SidebarContext";
import { Button, Badge, Collapse, ListItem } from "@mui/material";
import { PopoverWrapper, usePopover } from "../SidebarMenu";

interface SidebarMenuItemProps {
  children?: ReactNode;
  link?: any;
  icon?: any;
  badge?: string;
  open?: boolean;
  active?: boolean;
  name: string;
  openMenu: string[];
  setOpenMenu: Function;
  menus: any;
  hideText?: boolean;
}

const SidebarMenuItem: FC<SidebarMenuItemProps> = ({
  children,
  link,
  icon: Icon,
  badge,
  open: openParent,
  active,
  name,
  openMenu,
  setOpenMenu,
  menus,
  hideText,
  ...rest
}) => {
  const { toggleSidebar } = useContext(SidebarContext);
  const { anchorEl, open: popoverOpen, handlePopoverOpen, handlePopoverClose } = usePopover();

  useEffect(() => {
    if (active && menus?.children) {
      setOpenMenu((prev: any) => {
        return [...prev, menus];
      });
    }
  }, []);

  const toggleMenu = (menu: any): void => {
    setOpenMenu((prev: any) => {
      if (prev?.some((data: any) => data.name === menu.name)) {
        return prev?.filter((data: any) => data.name !== menu.name);
      } else {
        const foundItem = prev?.find((item: any) =>
          item?.children?.some((data: any) => data?.name === menu?.name)
        );
        if (foundItem) {
          return [foundItem, menu];
        } else {
          return [menu];
        }
      }
    });
  };

  if (children) {
    return (
      <ListItem
        component="div"
        className="Mui-children"
        key={name}
        {...rest}
        onMouseEnter={handlePopoverOpen}
        onMouseLeave={handlePopoverClose}
        sx={{ pointerEvents: "auto" }} // Ensure clicks are not blocked
      >
        <Button
          className={clsx({
            "Mui-active": openMenu.some((data: any) => data.name === name),
          })}
          startIcon={
            Icon ? (
              <Icon fill={active ? "#59C2B0" : "rgb(153, 158, 187)"} />
            ) : (
              <span>🚫</span> // Fallback icon for debugging
            )
          }
          onClick={() => {
            toggleMenu(menus);
          }}
          sx={{ cursor: "pointer", pointerEvents: "auto" }} // Cursor and clickability
        >
          {!hideText && name}
          {badge && <Badge badgeContent={badge} />}
        </Button>
        {hideText && ( 
          <PopoverWrapper
            id={`popover-${name}`}
            open={popoverOpen}
            anchorEl={anchorEl}
            onClose={handlePopoverClose}
            anchorOrigin={{
              vertical: "center",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "center",
              horizontal: "left",
            }}
            disableRestoreFocus
            sx={{ pointerEvents: "none" }}
          >
            {name}
          </PopoverWrapper>
        )}

        <Collapse in={openMenu.some((data: any) => data.name === name)}>
          {children}
        </Collapse>
      </ListItem>
    );
  }

  return (
    <ListItem
      component="div"
      key={name}
      {...rest}
      onMouseEnter={handlePopoverOpen}
      onMouseLeave={handlePopoverClose}
      sx={{ pointerEvents: "auto" }} // Ensure clicks are not blocked
    >
      <Button
        className={clsx({ "Mui-active": active })}
        component={RouterLink}
        onClick={() => {
          toggleSidebar();
          setOpenMenu((prev: any) => {
            const foundItem = prev?.find((item: any) =>
              item?.children?.some((data: any) => data?.name === menus?.name)
            );
            if (foundItem) {
              const parentItem = prev?.find((item: any) =>
                item?.children?.some(
                  (data: any) => data?.name === foundItem?.name
                )
              );
              if (parentItem) {
                return [parentItem, foundItem];
              } else {
                return [foundItem];
              }
            } else {
              return [];
            }
          });
        }}
        to={link}
        startIcon={
          Icon ? (
            <Icon fill={active ? "#59C2B0" : "rgb(153, 158, 187)"} />
          ) : (
            <span>🚫</span> // Fallback icon for debugging
          )
        }
        sx={{ cursor: "pointer", pointerEvents: "auto" }} // Cursor and clickability
      >
        {!hideText && name}
        {badge && <Badge badgeContent={badge} />}
      </Button>
      {hideText && ( // Show popup ONLY when sidebar is collapsed
        <PopoverWrapper
          id={`popover-${name}`}
          open={popoverOpen}
          anchorEl={anchorEl}
          onClose={handlePopoverClose}
          anchorOrigin={{
            vertical: "center",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "center",
            horizontal: "left",
          }}
          disableRestoreFocus
          sx={{ pointerEvents: "none" }}
        >
          {name}
        </PopoverWrapper>
      )}

    </ListItem>
  );
};
SidebarMenuItem.propTypes = {
  children: PropTypes.any,
  active: PropTypes.bool,
  link: PropTypes.any,
  icon: PropTypes.elementType,
  badge: PropTypes.string,
  open: PropTypes.bool,
  name: PropTypes.string.isRequired,
  hideText: PropTypes.bool,
};

SidebarMenuItem.defaultProps = {
  open: false,
  active: false,
  hideText: false,
};

export default SidebarMenuItem;
